import { useState, useEffect } from 'react';
import { networkMonitor } from 'utils/networkMonitor';
import Icon from 'components/AppIcon';

/**
 * NetworkStatusBar — fixed floating pill at the bottom center of the screen.
 *
 * Shows in two states:
 *   1. Offline  — red,   "No internet connection"
 *   2. Slow     — amber, "Loading, please wait…"
 *
 * Fades + slides up when visible, fades + slides down when resolved.
 * Does not shift page layout (position: fixed).
 */
const NetworkStatusBar = () => {
  const [slowCount, setSlowCount] = useState(networkMonitor.getSlowRequestCount());
  const [isOffline, setIsOffline] = useState(networkMonitor.getIsOffline());

  useEffect(() => {
    const unsub = networkMonitor.subscribe(() => {
      setSlowCount(networkMonitor.getSlowRequestCount());
      setIsOffline(networkMonitor.getIsOffline());
    });
    return () => { unsub(); };
  }, []);

  const isVisible = isOffline || slowCount > 0;

  // Keep in DOM so the hide animation plays
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={`
        fixed bottom-5 left-1/2 -translate-x-1/2 z-[500]
        transition-all duration-300 ease-in-out
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0 pointer-events-none'}
      `}
    >
      {isOffline ? (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg border text-sm font-medium bg-red-50 border-red-200 text-red-700">
          <Icon name="WifiOff" size={15} className="flex-shrink-0" />
          <span>No internet connection</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg border text-sm font-medium bg-amber-50 border-amber-200 text-amber-700">
          <Icon name="Loader" size={15} className="flex-shrink-0 animate-spin" />
          <span>Loading, please wait…</span>
        </div>
      )}
    </div>
  );
};

export default NetworkStatusBar;
